import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/16/solid';
import * as Popover from '@radix-ui/react-popover';
import React, { useEffect, useRef, useState } from 'react';

export interface ComboBoxOption {
  value: string;
  label: string;
  group?: string;
}

interface ComboBoxFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  isEditing: boolean;
  options: ComboBoxOption[];
  placeholder?: string;
  formatter?: (value: string) => string;
  hasError?: boolean;
  inputWidth?: string;
  popoverWidth?: string;
}

export const ComboBoxField: React.FC<ComboBoxFieldProps> = ({
  label,
  value,
  onChange,
  isEditing,
  options,
  placeholder = 'Select or type...',
  formatter,
  hasError = false,
  inputWidth = 'w-64',
  popoverWidth,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  // Find display label from value
  const displayText = options.find(opt => opt.value === value)?.label || value;

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group filtered options by their group property
  const groupedOptions = filteredOptions.reduce(
    (acc, option) => {
      const groupName = option.group || 'Other';
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(option);
      return acc;
    },
    {} as Record<string, ComboBoxOption[]>
  );

  // Check if search term matches any option value exactly
  const hasExactMatch =
    filteredOptions.length > 0 &&
    filteredOptions.some(
      opt => opt.value.toLowerCase() === searchTerm.toLowerCase()
    );

  // Show custom value hint when user has typed something and no exact match
  const showCustomHint = searchTerm.trim() !== '' && !hasExactMatch;

  // Reset highlight when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.querySelector(
        `[data-option-index="${highlightedIndex}"]`
      );
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      if (e.shiftKey && searchTerm.trim() !== '') {
        // Shift+Enter: use raw input as custom value
        e.preventDefault();
        onChange(searchTerm.trim());
        setOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      } else if (
        highlightedIndex >= 0 &&
        highlightedIndex < filteredOptions.length
      ) {
        // Enter with highlighted item: select it
        handleSelect(filteredOptions[highlightedIndex].value);
      } else if (filteredOptions.length > 0 && searchTerm.trim() !== '') {
        // Enter with no highlight but matches exist: select first
        handleSelect(filteredOptions[0].value);
      }
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchTerm('');
      setHighlightedIndex(-1);
    }
  };

  // The highlighted option to show in the hint
  const highlightedOption =
    highlightedIndex >= 0 && highlightedIndex < filteredOptions.length
      ? filteredOptions[highlightedIndex]
      : filteredOptions.length > 0
        ? filteredOptions[0]
        : null;

  return (
    <div className='flex justify-between items-center'>
      {label && <span className='text-gray-600'>{label}:</span>}
      {isEditing ? (
        <Popover.Root open={open} onOpenChange={handleOpenChange}>
          <Popover.Trigger asChild>
            <button
              type='button'
              className={`text-xs px-2 py-1 border rounded ${inputWidth} text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                hasError && !value
                  ? 'border-red-500 text-red-600'
                  : 'border-gray-300'
              }`}
            >
              {displayText || (
                <span className={hasError ? 'text-red-600' : 'text-gray-400'}>
                  {placeholder}
                </span>
              )}
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className={`${popoverWidth || inputWidth} bg-white border border-gray-300 rounded shadow-lg z-50`}
              sideOffset={5}
              align='start'
            >
              <div className='p-2'>
                <input
                  type='text'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder='Search or type...'
                  className='text-xs px-2 py-1 border border-gray-300 rounded w-full mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
                />
                {showCustomHint && (
                  <div className='px-2 py-1.5 text-xs text-gray-400 border border-gray-200 rounded mb-2'>
                    {highlightedOption && (
                      <div className='flex items-center gap-0.5 flex-wrap'>
                        <ArrowUpIcon className='w-3 h-3 inline' />
                        <ArrowDownIcon className='w-3 h-3 inline' />
                        <span>to select,</span>
                        <kbd className='font-medium'>Enter</kbd>
                        <span>to confirm</span>
                      </div>
                    )}
                    <div>
                      Press{' '}
                      <kbd className='font-medium'>Shift+Enter</kbd> to use "
                      {searchTerm}"
                    </div>
                  </div>
                )}
                <div className='max-h-64 overflow-y-auto' ref={listRef}>
                  {filteredOptions.length === 0 && !showCustomHint ? (
                    <div className='px-2 py-2 text-xs text-gray-500'>
                      No options found
                    </div>
                  ) : (
                    (() => {
                      let flatIndex = 0;
                      return Object.entries(groupedOptions).map(
                        ([groupName, groupOptions]) => (
                          <div key={groupName}>
                            {groupName !== 'Other' && (
                              <div className='px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-100 sticky top-0'>
                                {groupName}
                              </div>
                            )}
                            {groupOptions.map(opt => {
                              const idx = flatIndex++;
                              return (
                                <button
                                  key={opt.value}
                                  type='button'
                                  data-option-index={idx}
                                  onClick={() => handleSelect(opt.value)}
                                  onMouseEnter={() =>
                                    setHighlightedIndex(idx)
                                  }
                                  className={`w-full text-left px-2 py-1 text-xs cursor-pointer rounded ${
                                    idx === highlightedIndex
                                      ? 'bg-blue-100 font-medium'
                                      : opt.value === value
                                        ? 'bg-blue-50 font-medium'
                                        : 'hover:bg-blue-50'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        )
                      );
                    })()
                  )}
                </div>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      ) : (
        <span className='font-medium text-gray-800 text-right'>
          {formatter ? formatter(value) : displayText}
        </span>
      )}
    </div>
  );
};
